import React, {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Col, Form, Row } from 'antd';
import moment from 'moment';

import { ReactComponent as ArrowDownIcon } from 'app/assets/img/arrow-down-icon.svg';

import { selectTranslations } from 'app/pages/App/slice/selectors';
import RefundLayout from '../../components/Layout';
import { Title } from '../../components/Typo';
import StyledTable from 'app/components/StyledTable';
import TextCell from '../../components/TextCell';
import ActionCell from '../../components/ActionCell';
import { StyledCheckbox } from 'app/pages/Cart/components/PaymentInfo';
import { formatNumber, formatTimeFrame, getFromDateToDate } from 'utils/helper';
import {
  createRefundTicketsApi,
  getRefundReasonsApi,
  getRefundTicketsApi,
} from 'app/services/checkTicketApi';
import { LoadingContainer } from 'app/components/LoadingContainer';
import {
  REFUND_TICKETS_ALLOCATED_DEBT,
  REFUND_TICKETS_SELECTED,
  SESSION_MESSAGE,
} from 'utils/constants';
import { TICKET_STATUS } from 'app/pages/NewAccount/CheckTickets';
import ErrMsg from 'app/pages/NewAccount/components/ErrMsg';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import useScrollIntoElement from 'utils/hooks/useScrollIntoElement';
import RenderIf from 'app/components/RenderIf';
import { SelectContainer, StyledSelect, StyledTextArea } from './styled';
import useFetchData from 'app/hooks/useFetchData';

export enum CANCEL_REASONS {
  NOT_ACCORDING_TO_POLICY = 1,
  ACCORDING_TO_POLICY,
}

enum REASON_ERROR_CODE {
  MAX_200 = 'REASON_MAX_200',
  PENALTY_FEE = 'errorPenaltyFee',
  MIN_AMOUNT = 'errorMinAmount',
}

enum REFUND_TICKET_DATA_ERROR_CODE {
  AMOUNT_TICKET = 'amountTicket',
}

type GET_REASONS_ENABLE_STATE = (options?: {
  onComplete?: (reasonsEnable?: CANCEL_REASONS[]) => void;
}) => void;

export const renderTicketDetail = (info: (string | null | undefined)[]) => {
  return info.filter(i => i).join(', ');
};

const TicketRefundSelectedList = () => {
  const { state } = useLocation<any>();
  const history = useHistory();
  const [refundTicketData, setRefundTicketData] = useState<any[]>(
    state?.refundTicketData,
  );
  const [businessPartnerData, setBusinessPartnerData] = useState<
    Record<string, any>
  >(state?.businessPartnerData);
  const [isLoadedRefundTicketData, setIsLoadedRefundTicketData] = useState(
    !!refundTicketData,
  );
  const [isErrorMinMaxConfig, setIsErrorMinMaxConfig] = useState(
    state?.isErrorMinMaxConfig || false,
  );
  const [isLoading, setIsLoading] = useState(false);
  const orderId = refundTicketData?.[0]?.orderId;
  const thirdPartySubmitNumber = refundTicketData?.[0]?.thirdPartySubmitNumber;
  const translations = useSelector(selectTranslations);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const [cancelReason, setCancelReason] = useState<CANCEL_REASONS>();
  const [isSetInitCancelReason, setIsSetInitCancelReason] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [errCannotRefundData, setErrCannotRefundData] = useState<any[]>([]);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [reasonList, setReasonList] = useState<
    { label: string; value: string }[]
  >([]);

  const reasonErrorCode = useRef<string | null>(null);

  const [policyReasonForm] = Form.useForm();
  const policyRefundReason = Form.useWatch('refund_reason', policyReasonForm);

  const [notPolicyReasonForm] = Form.useForm();
  const notPolicyRefundReason = Form.useWatch(
    'refund_reason',
    notPolicyReasonForm,
  );

  const formUsed =
    cancelReason === CANCEL_REASONS.ACCORDING_TO_POLICY
      ? policyReasonForm
      : notPolicyReasonForm;

  const anchorPoint = useRef<HTMLDivElement>(null);
  const { onScrollIntoElement } = useScrollIntoElement({
    elementRef: anchorPoint,
  });

  const [cancelReasonList, setCancelReasonList] = useState([
    {
      transKey: 'b2b.txt.cancelAccordingToPolicy',
      value: CANCEL_REASONS.ACCORDING_TO_POLICY,
      enable: false,
    },
    {
      transKey: 'b2b.txt.cancelNotAccordingToPolicy',
      value: CANCEL_REASONS.NOT_ACCORDING_TO_POLICY,
      enable: false,
    },
  ]);

  const getReasonsEnableState = useCallback<GET_REASONS_ENABLE_STATE>(
    options => {
      const reasonsEnable: CANCEL_REASONS[] = [];
      if (!refundTicketData) return;
      if (!!businessPartnerData?.allowReturnWithoutPolicy)
        reasonsEnable.push(CANCEL_REASONS.NOT_ACCORDING_TO_POLICY);

      if (!refundTicketData) return;

      const isAnyTicketNoConfigPercentFee = refundTicketData.some(
        ticket => ticket.percent === null,
      );

      // check config penalty free
      if (!isAnyTicketNoConfigPercentFee) {
        const isAnyTicketHasRemainingLessThanMinConfig = refundTicketData.some(
          ticket =>
            ticket.numberOfTicketsRemaining !== 0 &&
            ticket.numberOfTicketsRemaining < ticket.show.minAmountB2b,
        );
        // check min amount config
        if (!isAnyTicketHasRemainingLessThanMinConfig) {
          const lastDayToCancelByPolicyOfTickets = refundTicketData.map(
            ticket => {
              const { toDate } = getFromDateToDate({
                catalog: { ...ticket?.catalog },
                date: { ...ticket?.dateFrom },
                show: { ...ticket?.show },
                product: { ...ticket?.product },
                ticket,
              });
              const dayExpired = ticket?.configTicketShow?.DayExpired;
              const timeExpired = ticket?.configTicketShow?.timeExpired;
              let deadline = moment(toDate, 'DD/MM/YYYY');
              if (dayExpired !== null && dayExpired !== undefined) {
                deadline = deadline.add(dayExpired, 'days'); // toDate + DayExpired
              } else {
                deadline = moment().add(1, 'days');
              }
              if (timeExpired) {
                const [h, m] = timeExpired.split(':');
                deadline = deadline.set({
                  hour: Number(h),
                  minute: Number(m),
                  second: 0,
                  millisecond: 0,
                });
              } else {
                deadline = deadline.endOf('day');
              }
              return deadline;
            },
          );

          const minLastDayToCancelByPolicy = moment.min(
            lastDayToCancelByPolicyOfTickets,
          );

          // check last day can do cancel by policy
          if (moment().isSameOrBefore(minLastDayToCancelByPolicy)) {
            reasonsEnable.push(CANCEL_REASONS.ACCORDING_TO_POLICY);
          }
        }
      }

      options?.onComplete?.(reasonsEnable);

      setCancelReasonList(prev =>
        prev.map(reason => ({
          ...reason,
          enable:
            isErrorMinMaxConfig &&
            reason.value === CANCEL_REASONS.ACCORDING_TO_POLICY
              ? false
              : reasonsEnable.includes(reason.value),
        })),
      );
    },
    [
      refundTicketData,
      businessPartnerData?.allowReturnWithoutPolicy,
      isErrorMinMaxConfig,
    ],
  );

  useEffect(() => {
    if (!isLoadedRefundTicketData) return;
    if (isSetInitCancelReason) {
      getReasonsEnableState({
        onComplete(reasonsEnable) {
          if (!reasonsEnable || !reasonsEnable.length) return;
          setCancelReason(
            reasonsEnable.includes(CANCEL_REASONS.ACCORDING_TO_POLICY)
              ? isErrorMinMaxConfig
                ? CANCEL_REASONS.NOT_ACCORDING_TO_POLICY
                : CANCEL_REASONS.ACCORDING_TO_POLICY
              : CANCEL_REASONS.NOT_ACCORDING_TO_POLICY,
          );
          setIsSetInitCancelReason(false);
        },
      });
    } else {
      getReasonsEnableState();
    }
  }, [
    getReasonsEnableState,
    isLoadedRefundTicketData,
    isSetInitCancelReason,
    isErrorMinMaxConfig,
  ]);

  const columns = useMemo(
    () => [
      {
        title: (
          <span style={{ paddingLeft: 8 }}>
            {translations?.['b2b.txt.ticketName'] ?? ''}
          </span>
        ),
        dataIndex: 'ticketName',
        key: 'ticketName',
        width: 125,
      },
      {
        title: translations?.['b2b.txt.ticketPark'] ?? '',
        dataIndex: 'ticketPark',
        key: 'ticketPark',
        width: 200,
      },
      {
        title: translations?.['b2b.txt.ticketType'] ?? '',
        dataIndex: 'ticketType',
        key: 'ticketType',
        width: 100,
      },
      {
        title: translations?.['b2b.txt.ticket-usage-from'] ?? '',
        dataIndex: 'usageDate',
        key: 'usageDate',
        width: 116,
      },
      {
        title: translations?.['b2b.txt.ticket-usage-to'] ?? '',
        dataIndex: 'ticketExpired',
        key: 'ticketExpired',
        width: 116,
      },
      {
        title: translations?.['b2b.txt.ticket-detail'] ?? '',
        dataIndex: 'ticketDetail',
        key: 'ticketDetail',
        width: 103,
      },
      {
        title: (
          <span style={{ textAlign: 'right', display: 'block' }}>
            {translations?.['b2b.txt.ticketAmount'] ?? ''}
          </span>
        ),
        dataIndex: 'ticketAmount',
        key: 'ticketAmount',
        width: 100,
      },
      {
        title: (
          <span style={{ textAlign: 'right', display: 'block' }}>
            {translations?.['b2b.txt.unitPrice'] ?? ''}
          </span>
        ),
        dataIndex: 'unitPrice',
        key: 'unitPrice',
        width: 110,
      },
      {
        title: (
          <span style={{ textAlign: 'right', display: 'block' }}>
            {translations?.['b2b.txt.totalAmount'] ?? ''}
          </span>
        ),
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 120,
      },
      {
        title: (
          <span style={{ textAlign: 'right', display: 'block' }}>
            {translations?.[
              cancelReason === CANCEL_REASONS.ACCORDING_TO_POLICY
                ? 'b2b.txt.penaltyAccordingToPolicy'
                : 'b2b.txt.penaltyRate'
            ] ?? ''}
          </span>
        ),
        dataIndex: 'penalty',
        key: 'penalty',
        width: 130,
      },
      {
        title: (
          <span style={{ textAlign: 'right', display: 'block' }}>
            {translations?.['b2b.txt.fine-amount'] ?? ''}
          </span>
        ),
        dataIndex: 'fineAmount',
        key: 'fineAmount',
        width: 110,
      },
      {
        title: translations?.['b2b.txt.action'] ?? '',
        dataIndex: 'action',
        key: 'action',
        width: 120,
      },
    ],
    [cancelReason, translations],
  );
  const fetchRefundTicketData = useCallback(
    async (ticketIds: (string | number)[]) => {
      const bpAllocatedDebt = sessionStorage.getItem(
        REFUND_TICKETS_ALLOCATED_DEBT,
      );
      const formData = new FormData();
      ticketIds.forEach(ticketId => {
        formData.append('tickets[]', String(ticketId));
      });

      if (!!bpAllocatedDebt) {
        formData.append('bpAllocatedDebt', bpAllocatedDebt);
      }
      try {
        setIsLoading(true);
        const resRefundTicket = await getRefundTicketsApi(formData);
        if (resRefundTicket?.status === 200) {
          const refundTicketData = resRefundTicket.data?.data;
          const businessPartnerData = resRefundTicket.data?.businessPartner;
          const isErrorMinMaxConfig =
            resRefundTicket.data?.isErrorMinMaxConfig || false;
          setRefundTicketData(refundTicketData);
          setBusinessPartnerData(businessPartnerData);
          setIsErrorMinMaxConfig(isErrorMinMaxConfig);
        }
      } catch (err: any) {
        if (err?.code === 400 && err?.message) {
          setErrMsg(err.message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const dataSource = useMemo(() => {
    const onUncheckTicket = async (ticketId: number) => {
      const nextTickets =
        refundTicketData?.filter(ticket => ticket.id !== ticketId) ?? [];
      setErrCannotRefundData(prev =>
        prev.filter(ticket => ticket.id !== ticketId),
      );
      if (!nextTickets.length) {
        setRefundTicketData([]);
        return;
      }
      const ticketIds = nextTickets
        .map(ticket => ticket.groupTickets || [])
        .flat();
      await fetchRefundTicketData(ticketIds);
    };

    return (
      refundTicketData?.map(ticket => {
        const { fromDate, toDate } = getFromDateToDate({
          catalog: { ...ticket?.catalog },
          date: { ...ticket?.dateFrom },
          show: { ...ticket?.show },
          product: { ...ticket?.product },
          ticket,
        });

        return {
          key: ticket?.id,
          ticketName: (
            <TextCell style={{ paddingLeft: 8 }}>
              {ticket?.product?.name}
            </TextCell>
          ),
          ticketPark: <TextCell>{ticket?.park?.name}</TextCell>,
          ticketType: <TextCell>{ticket?.catalog?.name}</TextCell>,
          usageDate: <TextCell>{fromDate}</TextCell>,
          ticketExpired: <TextCell>{toDate}</TextCell>,
          ticketDetail: (
            <TextCell>
              {renderTicketDetail([
                ticket?.product?.restaurant?.name,
                ticket?.ZoneName,
                formatTimeFrame(ticket?.product?.usedArea2Object?.label ?? ''),
              ])}
            </TextCell>
          ),
          ticketAmount: (
            <TextCell style={{ textAlign: 'right' }}>{ticket?.amount}</TextCell>
          ),
          unitPrice: (
            <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
              ticket?.originalPrice ?? 0,
            ).replaceAll(',', '.')}đ`}</TextCell>
          ),
          totalAmount: (
            <TextCell style={{ textAlign: 'right' }}>{`${formatNumber(
              ticket?.totalPrice ?? 0,
            ).replaceAll(',', '.')}đ`}</TextCell>
          ),
          penalty: (
            <TextCell style={{ textAlign: 'right' }}>
              {cancelReason === CANCEL_REASONS.ACCORDING_TO_POLICY
                ? `${ticket?.percent}%`
                : translations?.['b2b.txt.pendingApprove'] ?? ''}
            </TextCell>
          ),
          fineAmount: (
            <TextCell style={{ textAlign: 'right', textWrap: 'nowrap' }}>
              {cancelReason === CANCEL_REASONS.ACCORDING_TO_POLICY
                ? `${formatNumber(ticket?.penaltyFee ?? 0).replaceAll(
                    ',',
                    '.',
                  )}đ`
                : translations?.['b2b.txt.pendingApprove'] ?? ''}
            </TextCell>
          ),
          action: (
            <ActionCell
              type="primary"
              onClick={() => onUncheckTicket(ticket.id)}
              disabled={refundTicketData.length < 2}
            >
              {translations?.['b2b.btn.unchecked'] ?? ''}
            </ActionCell>
          ),
        };
      }) ?? []
    );
  }, [cancelReason, refundTicketData, translations]);

  const onCreateRefundTicket = async () => {
    if (isLoading || !executeRecaptcha || !cancelReason) return;
    setIsLoading(true);
    const tokenCaptcha = await executeRecaptcha('createRefundTicket');

    const formData = new FormData();
    refundTicketData
      .map(ticket => ticket.groupTickets)
      .flat(Infinity)
      .map(id => formData.append('tickets[]', id));
    formData.append('return_policy', cancelReason.toString());
    formData.append('captcha', tokenCaptcha);

    const bpAllocatedDebt =
      refundTicketData?.[0]?.businessPartnerAllocatedDebt?.id;

    if (bpAllocatedDebt !== null && bpAllocatedDebt !== undefined)
      formData.append('bpAllocatedDebt', bpAllocatedDebt);

    let reason = formUsed.getFieldValue('refund_reason');
    if (reason === 'OTHER')
      reason = formUsed.getFieldValue('other_refund_reason');
    formData.append('returnReason', reason);
    try {
      const res = await createRefundTicketsApi(formData);
      const resData = res.data;
      if (
        +resData?.order?.returnPolicy === CANCEL_REASONS.ACCORDING_TO_POLICY
      ) {
        history.push(`/ticket-refund/info/${resData.order.idHash}`, {
          refundTicketOrderData: resData,
        });
      } else {
        history.push(`/ticket-refund/pending-approve`, {
          refundTicketOrderData: resData,
        });
      }
    } catch (err: any) {
      if (err?.data?.errorCode === 'TICKET_CANNOT_REFUND') {
        setErrCannotRefundData(err?.data?.ticketNames);
        return;
      }
      if (err?.code === 400 && err?.message) setErrMsg(err.message);
      if (err?.data?.errorCode === REASON_ERROR_CODE.MAX_200)
        reasonErrorCode.current = err?.data?.errorCode;

      if (err?.data?.errorCode === REASON_ERROR_CODE.PENALTY_FEE) {
        reasonErrorCode.current = err?.data?.errorCode;
        const showHasError = err?.data?.item?.showId;
        setRefundTicketData(prev =>
          prev.map(item => {
            if (item?.show?.id !== showHasError) return item;
            return {
              ...item,
              percent: null,
            };
          }),
        );
      }

      if (err?.data?.errorCode === REASON_ERROR_CODE.MIN_AMOUNT) {
        reasonErrorCode.current = err?.data?.errorCode;
        const showHasError = err?.data?.item?.showId;
        setRefundTicketData(prev =>
          prev.map(item => {
            if (item?.show?.id !== showHasError) return item;
            return {
              ...item,
              show: {
                ...item,
                originalMinAmountB2b: err?.data?.item?.minAmount,
              },
            };
          }),
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const { isLoading: isLoadingRefundReason } = useFetchData<{
    data: { id: number; content: string }[];
  }>({
    actionApi: getRefundReasonsApi,
    onSuccess: ({ data }) => {
      const reasonList = data.map(reason => ({
        label: reason.content,
        value: reason.content,
      }));
      reasonList.push({
        label: translations?.['b2b.txt.otherReason'] ?? '',
        value: 'OTHER',
      });
      setReasonList(reasonList);
    },
    IIFE: translations?.['b2b.txt.otherReason'] ? true : false,
  });

  useEffect(() => {
    const checkTicketUsed = (tickets: any[]) => {
      return tickets.some(ticket => ticket.status === TICKET_STATUS.USED);
    };

    const checkTicketInvalidStatus = (tickets: any[]) => {
      return tickets.some(
        ticket =>
          ticket.status !== TICKET_STATUS.OUTDATE &&
          ticket.status !== TICKET_STATUS.UNUSE,
      );
    };

    const loadingRefundTicketData = async () => {
      if (isLoadedRefundTicketData || isLoading || !translations) return;
      const ticketIds = sessionStorage.getItem(REFUND_TICKETS_SELECTED);
      const bpAllocatedDebt = sessionStorage.getItem(
        REFUND_TICKETS_ALLOCATED_DEBT,
      );

      if (!ticketIds) {
        history.push('/check-tickets');
        return;
      }

      const formData = new FormData();
      ticketIds?.split(',').forEach((ticketId: any) => {
        formData.append('tickets[]', ticketId);
      });
      if (!!bpAllocatedDebt)
        formData.append('bpAllocatedDebt', bpAllocatedDebt);
      try {
        setIsLoading(true);
        const resRefundTicket = await getRefundTicketsApi(formData);
        if (resRefundTicket?.status === 200) {
          const refundTicketData = resRefundTicket.data?.data;
          const businessPartnerData = resRefundTicket.data?.businessPartner;
          const isErrorMinMaxConfig =
            resRefundTicket.data?.isErrorMinMaxConfig || false;
          if (checkTicketUsed(refundTicketData)) {
            setErrMsg(translations?.['b2b.msg.ticketHasBeenUsed'] ?? '');
          } else if (checkTicketInvalidStatus(refundTicketData)) {
            setErrMsg(
              translations?.['b2b.msg.ticketSelectInvalidStatus'] ?? '',
            );
          }
          setRefundTicketData(refundTicketData);
          setBusinessPartnerData(businessPartnerData);
          setIsErrorMinMaxConfig(isErrorMinMaxConfig);
        } else {
        }
      } catch (err: any) {
        if (
          err?.code === 400 &&
          err?.data?.errorCode ===
            REFUND_TICKET_DATA_ERROR_CODE.AMOUNT_TICKET &&
          err?.message
        ) {
          onOpenDialogNoti({
            type: 'ERROR',
            isShow: true,
            title: '',
            content: err?.message
              ?.replaceAll('\\n', '\n')
              ?.split('\n')
              ?.map(val => (
                <div>
                  {val}
                  <br />
                </div>
              )),
            closable: true,
            onCloseDialog: () => {
              onCloseDialogNoti();
            },
          });
        } else if (err?.code === 400 && err?.message) setErrMsg(err.message);
      } finally {
        setIsLoadedRefundTicketData(true);
        setIsLoading(false);
      }
    };

    loadingRefundTicketData();
  }, [
    history,
    isLoadedRefundTicketData,
    isLoading,
    translations,
    onOpenDialogNoti,
    onCloseDialogNoti,
  ]);

  useEffect(() => {
    // Remove refund ticket data on history stack to ensure while reload page,
    // it will get newest data
    const clearRefundTicketDataOnHistoryStack = () => {
      if (!state?.refundTicketData) return;
      const { pathname } = history.location;
      history.push(pathname);
    };

    clearRefundTicketDataOnHistoryStack();
  }, [history, state?.refundTicketData]);

  useEffect(() => {
    // if cancel order at page /ticket-refund/info,
    // then back to this component -> show msg cancel successfully
    const showCancelOrderSuccessMsg = () => {
      const sessionMsg = JSON.parse(
        sessionStorage.getItem(SESSION_MESSAGE) ?? '{}',
      );
      sessionStorage.removeItem(SESSION_MESSAGE);
      if (sessionMsg?.message) {
        onOpenDialogNoti({
          type: sessionMsg?.type ?? 'SUCCESS',
          isShow: true,
          title: '',
          content: sessionMsg?.message,
          buttons: [],
          closable: true,
          maskClosable: false,
          onCloseDialog: () => {
            onCloseDialogNoti();
          },
        });
      }
    };

    showCancelOrderSuccessMsg();
  }, [onCloseDialogNoti, onOpenDialogNoti]);

  useLayoutEffect(() => {
    const onHaveNoData = () => {
      if (
        !isLoadedRefundTicketData ||
        (refundTicketData && refundTicketData.length > 0)
      )
        return;
      history.push('/check-tickets');
    };

    onHaveNoData();
  }, [history, isLoadedRefundTicketData, refundTicketData]);

  return (
    <LoadingContainer loading={!refundTicketData || isLoading}>
      {refundTicketData ? (
        <RefundLayout
          title={translations?.['b2b.txt.listTicketRefund'] ?? ''}
          useActionBlock
          onBack={() => history.push('/check-tickets')}
          onNext={formUsed.submit}
          disabledNextBtn={
            (!!errMsg &&
              reasonErrorCode.current !== REASON_ERROR_CODE.PENALTY_FEE &&
              reasonErrorCode.current !== REASON_ERROR_CODE.MIN_AMOUNT) ||
            !!errCannotRefundData.length ||
            !cancelReason
          }
        >
          <div>
            <Row gutter={[92, 10]} style={{ marginTop: 10 }}>
              <Col>
                <Title
                  style={{
                    fontSize: 14,
                    textTransform: 'initial',
                  }}
                >
                  {translations?.['b2b.txt.order-code']}
                </Title>
              </Col>
              <Col>
                <Title
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    textTransform: 'initial',
                  }}
                >
                  {thirdPartySubmitNumber ?? orderId}
                </Title>
              </Col>
            </Row>
            <div
              id="reason_block"
              className="d-flex"
              style={{
                margin: '24px 0px',
                rowGap: 10,
                columnGap: 57,
                flexWrap: 'wrap',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Title style={{ fontSize: 14, textTransform: 'initial' }}>
                {translations?.['b2b.txt.reasonCancellation'] ?? ''}
              </Title>
              <div
                className="d-flex"
                style={{
                  transform: 'translateY(-6px)',
                  flexWrap: 'wrap',
                  rowGap: 6,
                  columnGap: 24,
                }}
              >
                {cancelReasonList.map(i => (
                  <div style={{ width: 222 }} key={i.value}>
                    <StyledCheckbox
                      checked={i.value === cancelReason}
                      value={i.value}
                      onChange={e => {
                        setCancelReason(e.target.value);
                        if (
                          reasonErrorCode.current ===
                            REASON_ERROR_CODE.PENALTY_FEE ||
                          reasonErrorCode.current ===
                            REASON_ERROR_CODE.MIN_AMOUNT
                        ) {
                          setErrMsg('');
                          reasonErrorCode.current = null;
                        }
                      }}
                      antCheckboxWrapperCSS={{
                        marginTop: '0 !important',
                      }}
                      disabled={!i.enable}
                    >
                      <span>{translations?.[i.transKey] ?? ''}</span>
                    </StyledCheckbox>
                    <RenderIf condition={i.value === cancelReason}>
                      <div style={{ marginTop: 16 }}>
                        <Form form={formUsed} onFinish={onCreateRefundTicket}>
                          <SelectContainer id={`${i.value}-container`} />
                          <Form.Item
                            name="refund_reason"
                            rules={[
                              {
                                required: true,
                                message:
                                  translations?.['b2b.msg.plsChooseReason'] ??
                                  '',
                              },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <StyledSelect
                              placeholder={
                                translations?.['b2b.txt.chooseReason'] ?? ''
                              }
                              style={{ height: 44 }}
                              suffixIcon={
                                <ArrowDownIcon style={{ opacity: 0.3 }} />
                              }
                              options={reasonList}
                              dropdownStyle={{
                                borderRadius: 8,
                                border: 'solid 1px #D9D9D9',
                                boxShadow: 'none',
                              }}
                              getPopupContainer={() =>
                                document.getElementById(`${i.value}-container`)!
                              }
                              disabled={!i.enable}
                            />
                          </Form.Item>
                          <RenderIf
                            condition={
                              (cancelReason ===
                              CANCEL_REASONS.ACCORDING_TO_POLICY
                                ? policyRefundReason
                                : notPolicyRefundReason) === 'OTHER'
                            }
                          >
                            <Form.Item
                              name="other_refund_reason"
                              rules={[
                                {
                                  required: true,
                                  message:
                                    translations?.['b2b.msg.plsEnterReason'] ??
                                    '',
                                },
                                {
                                  pattern: /^(?!\s*$).+/,
                                  message:
                                    translations?.['b2b.msg.plsEnterReason'] ??
                                    '',
                                },
                              ]}
                              validateFirst
                              validateTrigger={['onBlur']}
                              style={{ marginBottom: 0 }}
                            >
                              <StyledTextArea
                                style={{
                                  marginTop: 16,
                                  height: 103,
                                  resize: 'none',
                                }}
                                autoSize={false}
                                placeholder={
                                  translations?.[
                                    'b2b.txt.plsEnterRefundReason'
                                  ] ?? ''
                                }
                                maxLength={200}
                                onChange={() => {
                                  if (
                                    reasonErrorCode.current ===
                                    REASON_ERROR_CODE.MAX_200
                                  ) {
                                    setErrMsg('');
                                    reasonErrorCode.current = null;
                                  }
                                }}
                                onBlur={e => {
                                  const value = e.target.value;
                                  if (/^\s+$/.test(value)) {
                                    formUsed.setFieldValue(
                                      'other_refund_reason',
                                      '',
                                    );
                                  } else {
                                    formUsed.setFieldValue(
                                      'other_refund_reason',
                                      value.trim(),
                                    );
                                  }
                                }}
                              />
                            </Form.Item>
                          </RenderIf>
                        </Form>
                      </div>
                    </RenderIf>
                  </div>
                ))}
              </div>
            </div>
            {errMsg ? (
              <div style={{ marginBottom: 24, display: 'inline-block' }}>
                <ErrMsg>
                  <div>
                    {errMsg
                      ?.replaceAll('\\n', '\n')
                      ?.split('\n')
                      ?.map(val => (
                        <div>
                          {val}
                          <br />
                        </div>
                      ))}
                  </div>
                </ErrMsg>
              </div>
            ) : null}
            {!!errCannotRefundData?.length ? (
              <>
                {errCannotRefundData?.map((val, idx) => (
                  <Fragment key={idx}>
                    <div style={{ marginBottom: 24, display: 'inline-block' }}>
                      <ErrMsg>
                        {translations?.['b2b.txt.ticketCannotRefund']?.replace(
                          '${name}',
                          val?.name || '',
                        )}
                      </ErrMsg>
                    </div>
                    <br />
                  </Fragment>
                ))}
              </>
            ) : null}
            <div ref={anchorPoint} />
            <StyledTable
              columns={columns}
              dataSource={dataSource}
              emptyText={translations?.['b2b.txt.haveNoTicketSelected'] ?? ''}
              styleSlots={{
                th: {
                  fontWeight: 600,
                  fontSize: 14,
                  textAlign: 'left',
                },
              }}
              pagination={{
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ['20', '50', '100'],
                onChange: onScrollIntoElement,
              }}
            />
          </div>
        </RefundLayout>
      ) : null}
    </LoadingContainer>
  );
};

export default TicketRefundSelectedList;
